import { useEffect, useState } from "react";
import ModalBase from "../modals/ModalBase";
import { EVENT_TYPES, categoryLabel, eventCategoryForType, readHubThumbnail, submitEventRequest } from "../../js/theHub";

const EMPTY = {
  title: "",
  type: "city",
  date: "",
  time: "",
  location: "",
  address: "",
  host: "",
  url: "",
  description: "",
  why: "",
  contact: "",
  thumbnail: "",
};

export default function EventRequestModal({ isOpen, onClose, session, mode = "request" }) {
  const [form, setForm] = useState(EMPTY);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const correction = mode === "correction";

  useEffect(() => {
    if (!isOpen) return;
    setForm(EMPTY);
    setNote("");
    setError("");
  }, [isOpen, mode]);

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function onThumb(event) {
    const file = event.target.files?.[0];
    setError("");
    try {
      const thumbnail = await readHubThumbnail(file);
      setForm((current) => ({ ...current, thumbnail }));
    } catch (err) {
      setError(err.message);
    }
  }

  function submit(event) {
    event.preventDefault();
    setError("");
    try {
      submitEventRequest({ ...form, mode: correction ? "correction" : "request" }, session);
      setNote("Your event request has been submitted for review.");
      setForm(EMPTY);
    } catch (err) {
      setError(err.message || "We could not send that yet.");
    }
  }

  function close() {
    setNote("");
    setError("");
    onClose?.();
  }

  return (
    <ModalBase
      isOpen={isOpen}
      title={correction ? "Request an update" : "Request a listing"}
      onClose={close}
      wide
    >
      {note ? (
        <p className="confirm-banner">{note}</p>
      ) : (
        <form className="the-hub-form" onSubmit={submit}>
          <p className="muted">
            {correction
              ? "Tell us what should change. The program desk will review it."
              : "Ask the program desk to post an event for members. Nothing is published until it is reviewed."}
          </p>
          <label className="field">
            <span className="field-label">Event title</span>
            <input className="field-input" name="title" value={form.title} onChange={update} required />
          </label>
          <label className="field">
            <span className="field-label">Event type</span>
            <select className="field-select" name="type" value={form.type} onChange={update}>
              {EVENT_TYPES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <div className="the-hub-form__row">
            <label className="field">
              <span className="field-label">Date</span>
              <input className="field-input" type="date" name="date" value={form.date} onChange={update} required />
            </label>
            <label className="field">
              <span className="field-label">Time</span>
              <input className="field-input" name="time" value={form.time} onChange={update} placeholder="9:00 AM" />
            </label>
          </div>
          <label className="field">
            <span className="field-label">Location</span>
            <input className="field-input" name="location" value={form.location} onChange={update} placeholder="Venue or room name" />
          </label>
          <label className="field">
            <span className="field-label">Street address</span>
            <input className="field-input" name="address" value={form.address} onChange={update} placeholder="Street, city, state" />
          </label>
          <label className="field">
            <span className="field-label">Host organization</span>
            <input className="field-input" name="host" value={form.host} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">Event listing URL</span>
            <input
              className="field-input"
              name="url"
              value={form.url}
              onChange={update}
              placeholder="https://host-site.org/events/this-listing"
            />
          </label>
          <p className="muted">
            Optional. Use the host&apos;s page for this specific event when you have it. If left blank, approved
            listings link to the host events calendar or organization site for that source.
          </p>
          <p className="muted">This listing will be offered for the {categoryLabel(eventCategoryForType(form.type))} room.</p>
          <label className="field">
            <span className="field-label">Thumbnail (optional)</span>
            <input className="field-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onThumb} />
          </label>
          <label className="field">
            <span className="field-label">Description</span>
            <textarea className="field-textarea" name="description" rows={3} value={form.description} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">
              {correction ? "What needs to be corrected" : "Why this event should be included"}
            </span>
            <textarea className="field-textarea" name="why" rows={3} value={form.why} onChange={update} required />
          </label>
          <label className="field">
            <span className="field-label">Contact for verification</span>
            <input className="field-input" name="contact" value={form.contact} onChange={update} required />
          </label>
          {error ? <p className="the-hub-error">{error}</p> : null}
          <button className="primary-btn" type="submit">
            Submit for review
          </button>
        </form>
      )}
    </ModalBase>
  );
}
